import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Search as SearchIcon, X, FileText, Users, Image, Calendar, Loader2, Award, Film, Clock, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { api } from '@/services/api';
import { motion } from 'framer-motion';

const Search = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialQuery = searchParams.get('q') || '';
  
  const [searchTerm, setSearchTerm] = useState(initialQuery);
  const [inputValue, setInputValue] = useState(initialQuery);
  const [results, setResults] = useState({
    speakers: [],
    exhibitions: [],
    works: [],
    submissions: [],
    attendees: [],
    certificates: [],
    media: [],
    agenda: []
  });
  const [isSearching, setIsSearching] = useState(false);
  const [activeTab, setActiveTab] = useState('all');

  // Perform search when searchTerm changes
  useEffect(() => {
    if (!searchTerm || searchTerm.trim().length < 2) {
      setResults({ 
        speakers: [], exhibitions: [], works: [], submissions: [],
        attendees: [], certificates: [], media: [], agenda: []
      });
      return;
    }

    const performSearch = async () => {
      setIsSearching(true);
      try {
        const response = await api.get('/search', { 
          params: { 
            q: searchTerm,
            limit: 20 
          } 
        });

        if (response.data.success) {
          setResults({
            speakers: response.data.data.speakers || [],
            exhibitions: response.data.data.exhibitions || [],
            works: response.data.data.works || [],
            submissions: response.data.data.submissions || [],
            attendees: response.data.data.attendees || [],
            certificates: response.data.data.certificates || [],
            media: response.data.data.media || [],
            agenda: response.data.data.agenda || []
          });
        }
      } catch (error) {
        console.error('Search error:', error);
      } finally {
        setIsSearching(false);
      }
    };

    performSearch();
  }, [searchTerm]);

  // Update URL when search term changes
  const handleSearch = (e) => {
    e.preventDefault();
    if (inputValue.trim()) {
      setSearchParams({ q: inputValue.trim() });
      setSearchTerm(inputValue.trim());
    }
  };

  const handleResultClick = (type, item) => {
    switch(type) {
      case 'speakers':
        navigate(`/speakers`);
        break;
      case 'exhibitions':
        navigate(`/exhibitions`);
        break;
      case 'works':
        navigate(`/creative-works`);
        break;
      case 'submissions':
        navigate(`/my-account?tab=submissions`);
        break;
      case 'attendees':
        navigate(`/admin/attendees`);
        break;
      case 'certificates':
        navigate(`/my-account?tab=certificates`);
        break;
      case 'media':
        navigate(`/images`);
        break;
      case 'agenda':
        navigate(`/agenda`);
        break;
      default:
        break;
    }
  };

  const getTotalResults = () => {
    return Object.values(results).reduce((sum, arr) => sum + arr.length, 0);
  };

  const getFilteredResults = () => {
    if (activeTab === 'all') return results;
    return { [activeTab]: results[activeTab] };
  };

  const categoryInfo = {
    speakers: { icon: Users, label: 'วิทยากร', color: 'blue' },
    exhibitions: { icon: Image, label: 'นิทรรศการ', color: 'purple' },
    works: { icon: FileText, label: 'ผลงานสร้างสรรค์', color: 'green' },
    submissions: { icon: Calendar, label: 'การส่งผลงาน', color: 'orange' },
    attendees: { icon: Users, label: 'ผู้เข้าร่วม', color: 'pink' },
    certificates: { icon: Award, label: 'ใบประกาศนียบัตร', color: 'yellow' },
    media: { icon: Film, label: 'สื่อ (รูปภาพ/วิดีโอ)', color: 'red' },
    agenda: { icon: Clock, label: 'กำหนดการ', color: 'indigo' }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-blue-50 pt-[120px] pb-16">
      <div className="container mx-auto px-4 max-w-7xl">
        
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-12"
        >
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            ค้นหาข้อมูล
          </h1>
          <p className="text-gray-600">
            ค้นหาวิทยากร, นิทรรศการ, ผลงาน, และข้อมูลอื่นๆ
          </p>
        </motion.div>

        {/* Search Bar */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="mb-8"
        >
          <form onSubmit={handleSearch} className="max-w-3xl mx-auto">
            <div className="relative">
              <SearchIcon className="absolute left-6 top-1/2 transform -translate-y-1/2 text-gray-400 w-6 h-6" />
              <input
                type="text"
                placeholder="ค้นหา ข่าวสาร, วิทยากร, นิทรรศการ, ผลงาน..."
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                className="w-full pl-16 pr-24 py-5 text-lg border-2 border-gray-200 rounded-2xl focus:outline-none focus:border-purple-500 transition-colors shadow-lg bg-white"
                autoFocus
              />
              {inputValue && (
                <button
                  type="button"
                  onClick={() => {
                    setInputValue('');
                    setSearchTerm('');
                    setSearchParams({});
                  }}
                  className="absolute right-20 top-1/2 transform -translate-y-1/2 p-2 hover:bg-gray-100 rounded-full transition-colors"
                >
                  <X className="w-5 h-5 text-gray-400" />
                </button>
              )}
              <Button
                type="submit"
                className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-purple-600 hover:bg-purple-700 text-white px-6 py-2 rounded-xl"
              >
                ค้นหา
              </Button>
            </div>
          </form>
        </motion.div>

        {/* Loading State */}
        {isSearching && (
          <div className="text-center py-20">
            <Loader2 className="w-12 h-12 text-purple-600 animate-spin mx-auto mb-4" />
            <p className="text-gray-500 text-lg">กำลังค้นหา...</p>
          </div>
        )}

        {/* No Search Term */}
        {!searchTerm && !isSearching && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-20"
          >
            <SearchIcon className="w-24 h-24 text-gray-300 mx-auto mb-6" />
            <p className="text-gray-500 text-lg mb-2">พิมพ์คำที่ต้องการค้นหา</p>
            <p className="text-gray-400">อย่างน้อย 2 ตัวอักษร</p>
          </motion.div>
        )}

        {/* No Results */}
        {searchTerm && !isSearching && getTotalResults() === 0 && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center py-20"
          >
            <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <SearchIcon className="w-12 h-12 text-gray-400" />
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">
              ไม่พบผลลัพธ์
            </h2>
            <p className="text-gray-500 mb-1">ไม่พบผลลัพธ์สำหรับ "{searchTerm}"</p>
            <p className="text-sm text-gray-400">ลองค้นหาด้วยคำอื่นหรือตรวจสอบคำสะกด</p>
          </motion.div>
        )}

        {/* Results */}
        {searchTerm && !isSearching && getTotalResults() > 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
          >
            {/* Results Summary */}
            <div className="mb-8 flex items-center justify-between">
              <p className="text-gray-600">
                พบ <span className="font-bold text-purple-600">{getTotalResults()}</span> ผลลัพธ์สำหรับ 
                <span className="font-semibold text-gray-900"> "{searchTerm}"</span>
              </p>
            </div>

            {/* Category Tabs */}
            <div className="flex gap-2 mb-8 overflow-x-auto pb-2 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
              <button
                onClick={() => setActiveTab('all')}
                className={`px-6 py-3 rounded-xl font-medium text-sm whitespace-nowrap transition-all shadow-sm ${
                  activeTab === 'all'
                    ? 'bg-purple-600 text-white scale-105'
                    : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200'
                }`}
              >
                ทั้งหมด ({getTotalResults()})
              </button>
              {Object.entries(results).map(([type, items]) => {
                if (items.length === 0) return null;
                const info = categoryInfo[type];
                const Icon = info.icon;
                return (
                  <button
                    key={type}
                    onClick={() => setActiveTab(type)}
                    className={`px-6 py-3 rounded-xl font-medium text-sm whitespace-nowrap transition-all flex items-center gap-2 shadow-sm ${
                      activeTab === type
                        ? 'bg-purple-600 text-white scale-105'
                        : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    {info.label} ({items.length})
                  </button>
                );
              })}
            </div>

            {/* Results Grid */}
            <div className="space-y-8">
              {Object.entries(getFilteredResults()).map(([type, items]) => 
                items.length > 0 && (
                  <motion.div
                    key={type}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4 }}
                  >
                    <div className="flex items-center gap-3 mb-4">
                      {React.createElement(categoryInfo[type].icon, { 
                        className: `w-6 h-6 text-${categoryInfo[type].color}-600` 
                      })}
                      <h2 className="text-xl font-bold text-gray-900">
                        {categoryInfo[type].label}
                      </h2>
                      <span className="text-sm text-gray-500">
                        ({items.length})
                      </span>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {items.map((item, index) => {
                        // Dynamic content based on type
                        let title = '';
                        let subtitle = '';
                        let image = null;
                        
                        if (type === 'speakers') {
                          title = item.name || 'ไม่มีชื่อ';
                          subtitle = item.position || '';
                          image = item.image_url || item.photo_url;
                        } else if (type === 'exhibitions') {
                          title = item.name || 'ไม่มีชื่อ';
                          subtitle = item.description || '';
                          image = item.image_url || item.cover_image_url;
                        } else if (type === 'works') {
                          title = item.name || 'ไม่มีชื่อ';
                          subtitle = `โดย ${item.owner_name || 'ไม่ระบุ'}${item.category ? ` - ${item.category}` : ''}`;
                        } else if (type === 'submissions') {
                          title = item.title || 'ไม่มีชื่อ';
                          subtitle = `โดย ${item.author || 'ไม่ระบุ'} (${item.type === 'research' ? 'วิจัย' : 'สร้างสรรค์'})`;
                        } else if (type === 'attendees') {
                          title = item.name || `${item.first_name} ${item.last_name}`;
                          subtitle = `${item.organization || 'ไม่ระบุ'} - ${item.type === 'general' ? 'ทั่วไป' : item.type === 'research' ? 'วิจัย' : 'สร้างสรรค์'}`;
                        } else if (type === 'certificates') {
                          title = item.name || 'ไม่มีชื่อ';
                          subtitle = item.description || item.category || '';
                        } else if (type === 'media') {
                          title = item.name || 'ไม่มีชื่อ';
                          subtitle = `${item.type || ''} - ${item.event || ''}`;
                        } else if (type === 'agenda') {
                          title = item.title || 'ไม่มีชื่อ';
                          subtitle = `${item.speaker || ''} - ${item.location || ''}`;
                        }
                        
                        return (
                          <motion.button
                            key={index}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.3, delay: index * 0.05 }}
                            onClick={() => handleResultClick(type, item)}
                            className="group w-full text-left p-5 rounded-xl hover:bg-purple-50 transition-all border border-gray-200 hover:border-purple-300 hover:shadow-lg bg-white"
                          >
                            <div className="flex items-start gap-4">
                              {image && (
                                <div className="flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden bg-gray-100">
                                  <img 
                                    src={image} 
                                    alt={title}
                                    className="w-full h-full object-cover"
                                  />
                                </div>
                              )}
                              <div className="flex-1 min-w-0">
                                <div className="font-semibold text-gray-900 mb-1 group-hover:text-purple-700 transition-colors">
                                  {title}
                                </div>
                                {subtitle && (
                                  <div className="text-sm text-gray-500 line-clamp-2">
                                    {subtitle}
                                  </div>
                                )}
                              </div>
                              <ArrowRight className="w-5 h-5 text-gray-400 group-hover:text-purple-600 group-hover:translate-x-1 transition-all flex-shrink-0 mt-1" />
                            </div>
                          </motion.button>
                        );
                      })}
                    </div>
                  </motion.div>
                )
              )}
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default Search;
